import React, { useState, useEffect, FormEvent } from 'react'
import { Segment } from 'lib/ui'
import {
  Button,
  Container,
  Heading,
  Item,
  Message,
  Form,
  Input,
  Label,
} from 'lib/ui'
import Link from 'lib/components/Link'

import SEO from 'lib/components/SEO'
import Recall from 'lib/components/Recall'
import MakePicker from 'lib/components/Vehicle/MakePicker'
import ModelPicker from 'lib/components/Vehicle/ModelPicker'
import YearPicker from 'lib/components/Vehicle/YearPicker'
import { parseKeywordList } from 'lib/util/keywords'
import { trackEvent } from 'lib/util/analytics'
import useFirebase from 'lib/hooks/useFirebase'
import {
  searchKeywordsConfigure,
  searchVehicleConfigure,
} from 'lib/pages/Configure/search'
import ExamplePrompts from 'lib/pages/Configure/ExamplePrompts'
import { ConfigureExample } from 'lib/pages/Configure/examples'

type Mode = 'vehicle' | 'keywords'
type Prefilled = 'none' | 'example' | 'edited'

type SearchUiState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'match'; recalls: any[] }
  | { status: 'none' }
  | { status: 'error' }

export const TOPIC_PLACEHOLDERS: Record<string, string> = {
  allergies: 'e.g. eggs, peanuts, milk, gluten',
  food: 'e.g. salad, beef, salmon, cheese',
  children: 'e.g. stroller, pacifier, toy, crib',
  drugs: 'e.g. medication, brand name, ibuprofen',
  health: 'e.g. blood pressure, test kit, insulin',
  consumer: 'e.g. kettle, toaster, heater, battery',
}

export const DEFAULT_PLACEHOLDER = 'e.g. kettle, peanuts, crib, medicine'

export const CATEGORY_STATS: Record<string, string> = {
  vehicle: '141 vehicle recalls covering ~1.2 million vehicles so far in 2026.',
  food: '77 food recalls so far in 2026. Undeclared allergens account for nearly 40%.',
  allergies:
    '30 food allergen recalls so far in 2026. Milk, gluten, and eggs are among the top hazards.',
  children:
    '95 child and infant safety recalls so far in 2026 — about 2.7 recalls per week.',
  drugs:
    '332 health recalls so far in 2026 — about one a day across medications and devices.',
  health:
    '332 health recalls so far in 2026 — about one a day across medications and devices.',
  consumer:
    '605 consumer product recalls so far in 2026 — more than two a day.',
}

export const FALLBACK_STAT = 'More than 4 Canadian recalls a day.'

export const getPlaceholder = (topic: string): string => {
  return TOPIC_PLACEHOLDERS[topic] || DEFAULT_PLACEHOLDER
}

export const getCategoryStat = (topic: string): string => {
  return CATEGORY_STATS[topic] || FALLBACK_STAT
}

export const CATEGORY_SAMPLE_RECALLS: Record<string, any[]> = {
  vehicle: [
    {
      apiId: '2026330',
      recallId: '2026330',
      category: ['2'],
      date_published: 1783468800,
      start_date: 1783468800,
      parsed: {
        title:
          'TOYOTA issued a recall on GRAND HIGHLANDER HYBRID, TX500h, TX350, GRAND HIGHLANDER models',
        basic_details: {
          'Alert type': 'Recall',
          Category: ['SUV'],
          Department: 'Transport Canada',
          System: 'Structure',
          'Units affected': '61',
        },
      },
    },
    {
      apiId: '2026326',
      recallId: '2026326',
      category: ['2'],
      date_published: 1782950400,
      start_date: 1782950400,
      parsed: {
        title: 'HONDA issued a recall on the ODYSSEY model',
        basic_details: {
          'Alert type': 'Recall',
          Category: ['Minivan'],
          Department: 'Transport Canada',
          System: 'Visual System',
          'Units affected': '32289',
        },
      },
    },
    {
      apiId: '2026338',
      recallId: '2026338',
      category: ['2'],
      date_published: 1783987200,
      start_date: 1783987200,
      parsed: {
        title: 'FORD issued a recall on the FOCUS model',
        basic_details: {
          'Alert type': 'Recall',
          Category: ['Car'],
          Department: 'Transport Canada',
          System: 'Fuel System',
          'Units affected': '110138',
        },
      },
    },
  ],
  food: [
    {
      apiId: '11-21_82581',
      recallId: '82581',
      category: ['1'],
      date_published: 1788264000,
      start_date: 1788264000,
      parsed: {
        title:
          'Coaticook brand Aged Cheddar Cheese recalled due to Listeria monocytogenes',
        basic_details: {
          'Alert type': 'Food recall warning',
          Category: ['Food - Dairy'],
          Product: 'Cheese',
          Issue: 'Food - Microbial contamination - Listeria',
          'What to do':
            'Do not use, sell, serve or distribute the affected product.',
        },
      },
    },
    {
      apiId: '11-21_82519',
      recallId: '82519',
      category: ['1'],
      date_published: 1787313600,
      start_date: 1787313600,
      parsed: {
        title:
          'Compliments brand and Selection brand Corn Dogs recalled due to Listeria monocytogenes',
        basic_details: {
          'Alert type': 'Food recall warning',
          Category: ['Food - Meat and poultry'],
          Product: 'Corn Dogs',
          Issue: 'Food - Microbial contamination - Listeria',
          'What to do':
            'Do not use, sell, serve or distribute the affected products.',
        },
      },
    },
    {
      apiId: '11-21_82488',
      recallId: '82488',
      category: ['1'],
      date_published: 1786708800,
      start_date: 1786708800,
      parsed: {
        title:
          'Coaticook brand Mild Cheddar Curds - White, Frozen recalled due to Listeria monocytogenes',
        basic_details: {
          'Alert type': 'Food recall warning',
          Category: ['Food - Dairy'],
          Product: 'Mild Cheddar Curds',
          Issue: 'Food - Microbial contamination - Listeria',
          'What to do':
            'Do not use, sell, serve or distribute the affected product.',
        },
      },
    },
  ],
  allergies: [
    {
      apiId: '11-21_82604',
      recallId: '82604',
      category: ['1'],
      date_published: 1788609600,
      start_date: 1788609600,
      parsed: {
        title:
          'Smarter Snacks brand Vegan Protein Puff Chili & Lime Flavored recalled due to undeclared milk',
        basic_details: {
          'Alert type': 'Allergy alert',
          Category: ['Food - Snacks'],
          Product: 'Vegan Protein Puff',
          Issue: 'Food - Allergen - Milk',
          'What to do':
            'Do not consume recalled products to which you are allergic or sensitive.',
        },
      },
    },
    {
      apiId: '11-21_82523',
      recallId: '82523',
      category: ['1'],
      date_published: 1787313600,
      start_date: 1787313600,
      parsed: {
        title:
          'Kim Phat brand Seafood Medley recalled due to undeclared egg, fish, soy, and wheat',
        basic_details: {
          'Alert type': 'Allergy alert',
          Category: ['Food - Fish and seafood'],
          Product: 'Seafood Medley',
          Issue: 'Food - Allergen - Egg, Fish, Soy, Wheat',
          'What to do':
            'Do not consume recalled products to which you are allergic or sensitive.',
        },
      },
    },
    {
      apiId: '11-21_82492',
      recallId: '82492',
      category: ['1'],
      date_published: 1786968000,
      start_date: 1786968000,
      parsed: {
        title:
          'Olymel brand Cooked Turkey Breast Roast Honey & Dijon recalled due to undeclared mustard',
        basic_details: {
          'Alert type': 'Allergy alert',
          Category: ['Food - Meat and poultry'],
          Product: 'Cooked Turkey Breast Roast',
          Issue: 'Food - Allergen - Mustard',
          'What to do': 'Do not consume if you have an allergy to mustard.',
        },
      },
    },
  ],
  children: [
    {
      apiId: '11-21_82596',
      recallId: '82596',
      category: ['4'],
      date_published: 1788523200,
      start_date: 1788523200,
      parsed: {
        title: 'Spiral Spinning Toy recalled due to choking hazard',
        basic_details: {
          'Alert type': 'Consumer product recall',
          Category: ['Consumer products - Toys and games'],
          Product: 'Spiral Spinning Toy',
          Issue: 'Consumer products - Choking hazard',
          'What to do':
            'Immediately stop using the recalled product and safely dispose of it.',
        },
      },
    },
    {
      apiId: '11-21_82249',
      recallId: '82249',
      category: ['4'],
      date_published: 1788436800,
      start_date: 1788436800,
      parsed: {
        title:
          'Skip Hop Baby Sesame Street Elmo Silicone Teether recalled due to choking hazard',
        basic_details: {
          'Alert type': 'Consumer product recall',
          Category: ['Consumer products - Other'],
          Product: 'Skip Hop Baby Silicone Teether',
          Issue: 'Consumer products - Choking hazard',
          'What to do':
            'Immediately stop using the recalled product and contact manufacturer for refund.',
        },
      },
    },
    {
      apiId: '11-21_82495',
      recallId: '82495',
      category: ['4'],
      date_published: 1787227200,
      start_date: 1787227200,
      parsed: {
        title:
          'MEC Shuttlecraft Kid Carrier recalled due to potential fall hazard',
        basic_details: {
          'Alert type': 'Consumer product recall',
          Category: ['Consumer products - Other'],
          Product: 'MEC Shuttlecraft Kid Carrier',
          Issue: 'Consumer products - Fall hazard',
          'What to do':
            'Immediately stop using the recalled product and return it to a MEC store.',
        },
      },
    },
  ],
  drugs: [
    {
      apiId: '11-21_82496',
      recallId: '82496',
      category: ['3'],
      date_published: 1786795200,
      start_date: 1786795200,
      parsed: {
        title:
          'Two lots of Teva-Pregabalin 150 mg capsules recalled due to cross-contamination with the drug sertraline',
        basic_details: {
          'Alert type': 'Drug recall',
          Category: ['Health products - Drugs'],
          Product: 'Teva-Pregabalin 150 mg capsules',
          Issue: 'Health products - Contamination',
          'What to do':
            'Do not stop taking medication without speaking with your healthcare professional or pharmacist.',
        },
      },
    },
    {
      apiId: '11-21_82479',
      recallId: '82479',
      category: ['3'],
      date_published: 1786536000,
      start_date: 1786536000,
      parsed: {
        title:
          'pms-IRBESARTAN: Affected lot may contain pms-IRBESARTAN/HCTZ 150/12.5 mg tablets.',
        basic_details: {
          'Alert type': 'Drug recall',
          Category: ['Health products - Drugs'],
          Product: 'pms-IRBESARTAN 150 mg Tablet',
          Issue: 'Health products - Contamination',
          'What to do':
            'Consult your healthcare provider prior to discontinuing use of the affected product.',
        },
      },
    },
    {
      apiId: '11-21_82501',
      recallId: '82501',
      category: ['3'],
      date_published: 1787140800,
      start_date: 1787140800,
      parsed: {
        title: 'CISATRACURIUM BESYLATE INJECTION USP MULTIDOSE',
        basic_details: {
          'Alert type': 'Drug recall',
          Category: ['Health products - Drugs'],
          Product: 'CISATRACURIUM BESYLATE INJECTION',
          Issue: 'Health products - Product quality',
          'What to do':
            'Consult your healthcare provider for any health concerns.',
        },
      },
    },
  ],
  health: [
    {
      apiId: '11-21_82601',
      recallId: '82601',
      category: ['3'],
      date_published: 1788523200,
      start_date: 1788523200,
      parsed: {
        title: 'LIFEPAK ® 35 Monitor/Defibrillator',
        basic_details: {
          'Alert type': 'Medical device recall',
          Category: ['Health products - Medical devices'],
          Product: 'LIFEPAK ® 35 Monitor/Defibrillator',
          Issue: 'Medical devices - Performance',
          'What to do':
            'Contact the manufacturer if you require additional information.',
        },
      },
    },
    {
      apiId: '11-21_82479',
      recallId: '82479',
      category: ['3'],
      date_published: 1786536000,
      start_date: 1786536000,
      parsed: {
        title:
          'pms-IRBESARTAN: Affected lot may contain pms-IRBESARTAN/HCTZ 150/12.5 mg tablets.',
        basic_details: {
          'Alert type': 'Drug recall',
          Category: ['Health products - Drugs'],
          Product: 'pms-IRBESARTAN 150 mg Tablet',
          Issue: 'Health products - Contamination',
          'What to do':
            'Consult your healthcare provider prior to discontinuing use of the affected product.',
        },
      },
    },
    {
      apiId: '11-21_82496',
      recallId: '82496',
      category: ['3'],
      date_published: 1786795200,
      start_date: 1786795200,
      parsed: {
        title:
          'Two lots of Teva-Pregabalin 150 mg capsules recalled due to cross-contamination with the drug sertraline',
        basic_details: {
          'Alert type': 'Drug recall',
          Category: ['Health products - Drugs'],
          Product: 'Teva-Pregabalin 150 mg capsules',
          Issue: 'Health products - Contamination',
          'What to do':
            'Do not stop taking medication without speaking with your healthcare professional or pharmacist.',
        },
      },
    },
  ],
  consumer: [
    {
      apiId: '11-21_82582',
      recallId: '82582',
      category: ['4'],
      date_published: 1788436800,
      start_date: 1788436800,
      parsed: {
        title:
          'DUNLOP RigPro full safety boots recalled due to electric shock hazard',
        basic_details: {
          'Alert type': 'Consumer product recall',
          Category: ['Consumer products - Footwear'],
          Product: 'Dunlop RigPro safety boots',
          Issue: 'Consumer products - Electrical hazard',
          'What to do':
            'Immediately stop using the recalled product and return it for a refund.',
        },
      },
    },
    {
      apiId: '11-21_82580',
      recallId: '82580',
      category: ['4'],
      date_published: 1788436800,
      start_date: 1788436800,
      parsed: {
        title:
          'Amana Through the Wall (TTW) air conditioners or heat pumps recalled due to fire hazard',
        basic_details: {
          'Alert type': 'Consumer product recall',
          Category: ['Consumer products - Specialized products'],
          Product: 'Amana TTW air conditioners',
          Issue: 'Consumer products - Fire hazard',
          'What to do':
            'Immediately stop using the recalled product and contact Daikin Comfort Technologies.',
        },
      },
    },
    {
      apiId: '11-21_82579',
      recallId: '82579',
      category: ['4'],
      date_published: 1788436800,
      start_date: 1788436800,
      parsed: {
        title: 'Branch Ergonomic Chairs recalled due to fall hazard',
        basic_details: {
          'Alert type': 'Consumer product recall',
          Category: ['Consumer products - Furniture'],
          Product: 'Ergonomic Chairs',
          Issue: 'Consumer products - Fall hazard',
          'What to do':
            'Immediately stop using the recalled product and contact Branch for a free repair kit.',
        },
      },
    },
  ],
}

export const getCategorySampleRecalls = (topic: string): any[] => {
  return CATEGORY_SAMPLE_RECALLS[topic] || CATEGORY_SAMPLE_RECALLS.consumer
}

const parseSearchParams = () => {
  if (typeof window === 'undefined' || !window.location?.search) {
    return {
      topic: 'all',
      mode: '',
      make: '',
      model: '',
      year: '',
      q: '',
      step: 1,
    }
  }
  const params = new URLSearchParams(window.location.search)
  const stepParam = params.get('step')
  return {
    topic: params.get('topic') || 'all',
    mode: params.get('mode') || '',
    make: params.get('make') || '',
    model: params.get('model') || '',
    year: params.get('year') || '',
    q: params.get('q') || params.get('keywords') || '',
    step: stepParam === '2' ? 2 : 1,
  }
}

const Configure: React.FC = () => {
  const firebase = useFirebase()
  const [step, setStep] = useState<1 | 2>(1)
  const [topic, setTopic] = useState<string>('all')
  const [mode, setMode] = useState<Mode>('keywords')
  const [make, setMake] = useState<string>('')
  const [model, setModel] = useState<string>('')
  const [year, setYear] = useState<string>('')
  const [keywords, setKeywords] = useState<string>('')
  const [hydrated, setHydrated] = useState<boolean>(false)
  const [searchUi, setSearchUi] = useState<SearchUiState>({ status: 'idle' })
  const [prefilled, setPrefilled] = useState<Prefilled>('none')
  const [fieldsGlow, setFieldsGlow] = useState(false)
  const [glowRun, setGlowRun] = useState(0)

  useEffect(() => {
    if (!fieldsGlow) return
    const timer = window.setTimeout(() => setFieldsGlow(false), 1200)
    return () => window.clearTimeout(timer)
  }, [fieldsGlow, glowRun])

  const noteEdit = () => {
    setPrefilled(current => (current === 'example' ? 'edited' : current))
  }

  // ModelPicker keeps a model value that is not in its list yet (options stay
  // empty until site/stats loads), so make, model and year update together.
  const applyExample = (
    example: ConfigureExample,
    options: { glow: boolean },
  ) => {
    if (example.mode === 'vehicle' && example.vehicle) {
      setMode('vehicle')
      setTopic(example.topic)
      setKeywords('')
      setMake(example.vehicle.make)
      setModel(example.vehicle.model)
      setYear(example.vehicle.year)
    } else {
      setMode('keywords')
      setTopic(example.topic)
      setKeywords(example.keywords.join(', '))
      setMake('')
      setModel('')
      setYear('')
    }
    setPrefilled('example')
    if (options.glow) {
      setFieldsGlow(true)
      setGlowRun(n => n + 1)
    } else {
      setFieldsGlow(false)
    }
  }

  useEffect(() => {
    const params = parseSearchParams()
    if (params.topic) setTopic(params.topic)
    const initialMode: Mode =
      params.mode === 'vehicle' || params.mode === 'keywords'
        ? (params.mode as Mode)
        : params.topic === 'vehicle'
          ? 'vehicle'
          : 'keywords'
    setMode(initialMode)

    if (params.make) setMake(params.make)
    if (params.model) setModel(params.model)
    if (params.year) setYear(params.year)
    if (params.q) setKeywords(params.q)

    const isVehicleMode = initialMode === 'vehicle'
    const hasValidInput = isVehicleMode
      ? Boolean(params.make && params.make.trim())
      : Boolean(params.q && params.q.trim())

    if (params.step === 2 && hasValidInput) {
      setStep(2)
    }

    setHydrated(true)

    trackEvent('configure_view', { topic: params.topic })
  }, [])

  // Persist queries and step in URL so back/forward retains exact state
  useEffect(() => {
    if (!hydrated || typeof window === 'undefined') return

    const params = new URLSearchParams()
    if (topic && topic !== 'all') params.set('topic', topic)
    if (mode === 'vehicle') {
      params.set('mode', 'vehicle')
      if (make && make.trim()) params.set('make', make.trim())
      if (model && model.trim()) params.set('model', model.trim())
      if (year && year.trim()) params.set('year', year.trim())
    } else {
      if (topic === 'vehicle') {
        params.set('mode', 'keywords')
      }
      if (keywords && keywords.trim()) params.set('q', keywords.trim())
    }
    if (step === 2) {
      params.set('step', '2')
    }

    const queryStr = params.toString()
    const targetUrl = queryStr ? `/configure?${queryStr}` : '/configure'
    const currentUrl = window.location.pathname + window.location.search

    if (currentUrl !== targetUrl) {
      window.history.replaceState({}, '', targetUrl)
    }
  }, [hydrated, topic, mode, make, model, year, keywords, step])

  const isValid =
    mode === 'vehicle'
      ? Boolean(make && make.trim())
      : Boolean(keywords && keywords.trim())

  useEffect(() => {
    if (step !== 2 || !isValid) {
      setSearchUi({ status: 'idle' })
      return
    }

    let cancelled = false
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)

    setSearchUi({ status: 'loading' })

    const runSearch = async () => {
      const outcome =
        mode === 'vehicle'
          ? await searchVehicleConfigure(
              firebase,
              make,
              model,
              year,
              controller.signal,
            )
          : await searchKeywordsConfigure(keywords, topic, controller.signal)

      clearTimeout(timeoutId)
      if (cancelled) return

      if (outcome.result === 'error') {
        setSearchUi({ status: 'error' })
      } else if (outcome.result === 'none') {
        setSearchUi({ status: 'none' })
      } else {
        setSearchUi({ status: 'match', recalls: outcome.recalls })
      }

      trackEvent('configure_result', {
        result: outcome.result,
        count: outcome.count,
        input_type: mode,
        topic,
      })
    }

    runSearch()

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
      controller.abort()
    }
  }, [step, mode, keywords, make, model, year, topic, isValid, firebase])

  const handleNext = (e?: FormEvent) => {
    if (e) e.preventDefault()
    if (!isValid) return

    trackEvent('configure_submit', {
      input_type: mode,
      topic,
      prefilled,
    })
    setStep(2)
  }

  const formatKeywordsDescription = (rawKeywords: string): string => {
    const list = parseKeywordList(rawKeywords)
    if (list.length > 1) {
      const quoted = list.map(k => `'${k}'`)
      return `${quoted.slice(0, -1).join(', ')} or ${quoted[quoted.length - 1]}`
    }
    return `'${rawKeywords.trim()}'`
  }

  const vehicleParts = [year, make, model].filter(Boolean).join(' ')
  const itemDescription =
    mode === 'vehicle'
      ? `your ${vehicleParts || make}`
      : formatKeywordsDescription(keywords)

  const activeTopic =
    mode === 'vehicle' ? 'vehicle' : topic === 'vehicle' ? 'all' : topic
  const statProof = getCategoryStat(activeTopic)
  const sampleRecalls = getCategorySampleRecalls(activeTopic)
  const sampleAlertRecall = sampleRecalls[0]
  const recentRecalls = sampleRecalls.slice(1)

  const ctaLabel = `Keep watching ${itemDescription} for me`
  const priceLine = 'CA$3.99/mo · cancel anytime'

  const buildSubscribeUrl = () => {
    if (mode === 'vehicle') {
      let href = '/subscribe?type=vehicle'
      if (make && make.trim())
        href += `&make=${encodeURIComponent(make.trim())}`
      if (model && model.trim())
        href += `&model=${encodeURIComponent(model.trim())}`
      if (year && year.trim())
        href += `&year=${encodeURIComponent(String(year).trim())}`
      return href
    }
    const kw = keywords.trim()
    let href = kw
      ? `/subscribe?type=keywords&q=${encodeURIComponent(kw)}`
      : '/subscribe'
    if (topic && topic !== 'all') {
      href +=
        (href.includes('?') ? '&' : '?') + `topic=${encodeURIComponent(topic)}`
    }
    return href
  }

  return (
    <>
      <SEO
        title="Configure your recall alerts | Recall Monitor"
        description="Configure alerts for vehicles or products you own. Instant email and SMS notifications when Canadian recalls are issued."
        canonical="https://recallmonitor.ca/configure"
        robots="noindex,follow"
      />
      <Container text style={{ marginTop: '2rem', marginBottom: '3rem' }}>
        {step === 1 ? (
          <>
            <Heading as="h1" textAlign="center">
              What should we watch?
            </Heading>
            <Heading
              as="h4"
              textAlign="center"
              color="grey"
              style={{ marginTop: 0, marginBottom: '2rem' }}
            >
              We monitor Canadian recall databases 24/7 and alert you the moment
              a recall is issued.
            </Heading>
            <Segment padded="very" raised>
              <Form size="large" onSubmit={handleNext}>
                <ExamplePrompts onFilled={applyExample} />
                <Form.Group
                  inline
                  style={{ marginBottom: '1.5rem', justifyContent: 'center' }}
                >
                  <label style={{ marginRight: '1rem' }}>What to watch:</label>
                  <Form.Radio
                    id="watch-vehicle"
                    label="A vehicle"
                    name="watch-mode"
                    value="vehicle"
                    checked={mode === 'vehicle'}
                    onChange={() => {
                      noteEdit()
                      setMode('vehicle')
                    }}
                  />
                  <Form.Radio
                    id="watch-keywords"
                    label="Keywords"
                    name="watch-mode"
                    value="keywords"
                    checked={mode === 'keywords'}
                    onChange={() => {
                      noteEdit()
                      setMode('keywords')
                    }}
                  />
                </Form.Group>

                <div
                  data-testid="configure-fields"
                  className={fieldsGlow ? 'rm-prefill-glow' : undefined}
                >
                  {mode === 'vehicle' ? (
                    <>
                      <MakePicker
                        value={make}
                        onChange={(_e, data) => {
                          noteEdit()
                          setMake(data.value)
                        }}
                      />
                      <ModelPicker
                        make={make}
                        model={model}
                        onChange={(_e, data) => {
                          noteEdit()
                          setModel(data.value)
                        }}
                      />
                      <YearPicker
                        value={
                          year !== '' && /^\d+$/.test(year) ? Number(year) : ''
                        }
                        onChange={(_e, data) => {
                          noteEdit()
                          setYear(String(data.value))
                        }}
                      />
                    </>
                  ) : (
                    <Form.Field>
                      <label htmlFor="keywords-input">Keywords to watch</label>
                      <Input
                        input={{
                          id: 'keywords-input',
                          'data-testid': 'keywords-input',
                        }}
                        fluid
                        placeholder={getPlaceholder(topic)}
                        value={keywords}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          noteEdit()
                          setKeywords(e.target.value)
                        }}
                      />
                      <div
                        data-testid="keywords-help"
                        style={{
                          color: '#666',
                          fontSize: '0.85rem',
                          marginTop: '0.35rem',
                        }}
                      >
                        Separate several terms with commas, e.g. &quot;losartan,
                        valsartan, amlodipine&quot;. Matching is on the recall
                        title.
                      </div>
                    </Form.Field>
                  )}
                </div>

                <Button
                  primary
                  fluid
                  size="large"
                  type="submit"
                  disabled={!isValid}
                  style={{ marginTop: '1.5rem' }}
                >
                  Next
                </Button>
                <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
                  <Link href={buildSubscribeUrl()} passHref>
                    <a
                      style={{
                        color: '#666',
                        fontSize: '0.95rem',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        trackEvent('configure_cta_click', {
                          placement: 'skip',
                          topic,
                          input_type: mode,
                        })
                      }
                    >
                      Skip preview and subscribe now — CA$3.99/mo
                    </a>
                  </Link>
                </div>
              </Form>
            </Segment>
          </>
        ) : (
          <>
            <Heading as="h1" textAlign="center">
              Your Monitor Preview
            </Heading>

            {searchUi.status === 'loading' && (
              <div data-testid="search-result">
                <p
                  style={{
                    color: '#666',
                    fontSize: '0.95rem',
                    margin: '1rem 0',
                  }}
                >
                  Checking Canadian recalls for {itemDescription}…
                </p>
              </div>
            )}
            {searchUi.status === 'match' && (
              <div data-testid="search-result" style={{ margin: '1.5rem 0' }}>
                <Heading as="h3" style={{ marginBottom: '0.75rem' }}>
                  Recalls found for {itemDescription}
                </Heading>
                <Item.Group divided relaxed>
                  {searchUi.recalls.map(recall => (
                    <Recall data={recall} key={recall.apiId} itemMeta={null} />
                  ))}
                </Item.Group>
              </div>
            )}
            {searchUi.status === 'none' && (
              <div data-testid="search-result" style={{ margin: '1.5rem 0' }}>
                <Message>
                  <p>
                    No Canadian recalls found for {itemDescription} right now.
                  </p>
                  <p>We&apos;ll alert you if one appears.</p>
                </Message>
              </div>
            )}

            <Message
              info
              style={{
                textAlign: 'center',
                fontSize: '1.05rem',
                margin: '1.5rem 0',
              }}
            >
              <p>{statProof}</p>
            </Message>

            {/* Top Primary CTA Button - right below the blue stat line */}
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <Link href={buildSubscribeUrl()} passHref>
                <Button
                  as="a"
                  primary
                  fluid
                  size="big"
                  style={{ margin: '0 auto' }}
                  onClick={() =>
                    trackEvent('configure_cta_click', {
                      placement: 'top',
                      topic,
                      input_type: mode,
                    })
                  }
                >
                  {ctaLabel}
                </Button>
              </Link>
              <p
                style={{
                  color: '#666',
                  fontSize: '0.9rem',
                  marginTop: '0.5rem',
                }}
              >
                {priceLine}
              </p>
            </div>

            {/* Sample alert: real past recall from category list (inert) */}
            <div style={{ margin: '2rem 0' }}>
              <Heading
                as="h4"
                color="grey"
                style={{ marginBottom: '0.75rem', fontWeight: 500 }}
              >
                Here&apos;s what an alert looks like (a real past recall):
              </Heading>
              <Segment
                data-testid="sample-alert"
                padded
                style={{
                  background: '#fafafa',
                  border: '1px solid #d4d4d5',
                  borderRadius: '6px',
                }}
              >
                <Label color="teal">Sample alert</Label>
                <Item.Group>
                  <Recall
                    data={sampleAlertRecall}
                    itemMeta={null}
                    interactive={false}
                  />
                </Item.Group>
              </Segment>
            </div>

            {/* Recent Real Recalls in this category using canonical Recall view */}
            <div style={{ margin: '2.5rem 0' }}>
              <Heading as="h3" style={{ marginBottom: '0.25rem' }}>
                Recent Canadian recalls in this category
              </Heading>
              <p style={{ color: '#666', marginBottom: '1.25rem' }}>
                Real safety recalls issued recently by Canadian regulators:
              </p>
              <Item.Group divided relaxed>
                {recentRecalls.map(recall => (
                  <Recall data={recall} key={recall.apiId} itemMeta={null} />
                ))}
              </Item.Group>
            </div>

            <Segment
              secondary
              textAlign="center"
              style={{ margin: '2rem 0', padding: '1.5rem' }}
            >
              <p style={{ fontSize: '1.1rem', lineHeight: '1.5', margin: 0 }}>
                Don&apos;t wait for a letter in the mail. We will monitor
                Canadian government databases 24/7 for {itemDescription}. If a
                match is found, you&apos;ll be the first to know.
              </p>
            </Segment>

            {/* Bottom Primary CTA Button + Change what to watch */}
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <Link href={buildSubscribeUrl()} passHref>
                <Button
                  as="a"
                  primary
                  fluid
                  size="big"
                  style={{ marginTop: '1.5rem', marginBottom: '1rem' }}
                  onClick={() =>
                    trackEvent('configure_cta_click', {
                      placement: 'bottom',
                      topic,
                      input_type: mode,
                    })
                  }
                >
                  {ctaLabel}
                </Button>
              </Link>
              <p
                style={{
                  color: '#666',
                  fontSize: '0.9rem',
                  marginTop: '0.5rem',
                }}
              >
                {priceLine}
              </p>
              <div style={{ marginTop: '1rem' }}>
                <Button basic size="small" onClick={() => setStep(1)}>
                  Change what to watch
                </Button>
              </div>
            </div>
          </>
        )}
      </Container>
    </>
  )
}

export default Configure
